import React, { PropsWithChildren, useCallback, useEffect } from "react";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { useMerchantUser } from "../../hooks/useMerchantUser";
import { SegmentContext } from "./SegmentContext";
import { usePathname } from "next/navigation";
import { useOnboardingMerchant } from "../../hooks/api/useOnboardingMerchant";
import {
  OneTrustCookieMissingReporter,
  useOneTrustPerformanceGate,
} from "cookie-consent";
import { useMerchantOneTrustSegment } from "merchant-core";

/**
 * Segment analytics is gated by Performance consent (C0002) when OneTrust is
 * configured. Without NEXT_PUBLIC_ONETRUST_ID, MOH keeps legacy analytics
 * loading behavior.
 *
 * Consent model in MP/MOH:
 *   - The banner is suppressed in this app — consent is captured upstream
 *     (Auth0 login or zip.co marketing) and shared via the OptanonConsent
 *     cookie on .zip.co.
 *   - The gate reacts to runtime OneTrust updates (OneTrustGroupsUpdated):
 *     when Performance is off, tracking calls are skipped and identity is
 *     reset; when Performance returns, tracking resumes.
 *   - Segment SDK load is singleton-scoped to avoid repeated load() calls.
 */
export const SegmentProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const { merchantId } = useMerchantUser();
  const { data: merchant } = useOnboardingMerchant();
  const pathname = usePathname();
  const { oneTrustStatus, performanceAllowed } = useOneTrustPerformanceGate({
    domainScript: process.env.NEXT_PUBLIC_ONETRUST_ID,
  });
  const loadSegment = useCallback(
    (segmentWriteKey: string) =>
      AnalyticsBrowser.load({ writeKey: segmentWriteKey }),
    [],
  );
  const { analytics, trackOneTrustCookieMissing } =
    useMerchantOneTrustSegment<AnalyticsBrowser>({
      performanceAllowed,
      merchantId,
      pathname,
      load: loadSegment,
    });

  // logging contexts
  const businessInfo = merchant?.businessInfo;
  useEffect(() => {
    if (!analytics || !merchantId) {
      return;
    }

    analytics?.group({
      merchantId,
      merchantTier: businessInfo?.merchantTier,
      businessType: businessInfo?.businessType,
      annualRevenue: businessInfo?.annualRevenue,
      averageOrderValue: businessInfo?.averageOrderValue,
      industry: businessInfo?.industry,
      secondaryIndustry: businessInfo?.secondaryIndustry,
    });
  }, [analytics, merchantId, businessInfo]);

  return (
    <SegmentContext.Provider value={analytics}>
      <OneTrustCookieMissingReporter
        enabled={oneTrustStatus.shouldReportMissingCookie && Boolean(analytics)}
        merchantId={merchantId}
        surface="MOH"
        track={trackOneTrustCookieMissing}
      />
      {children}
    </SegmentContext.Provider>
  );
};
